import { searchPlace, getPlaceDetails } from '../services/api/placesApi.ts';

const STATIC_TOKEN = 'j/w1pkmuJdeL5MslaCM6cISLG/rur+E/Pt9/mHtrdBc=';

export interface VendorServiceDescription {
  serviceType: string;
  serviceDescriptions: string[];
}

export interface VendorAboutAttribute {
  aboutItemName: string;
  aboutItemDescription: string;
}

export interface ImageMetadata {
  metadataValuesList: {
    key: string;
    value: string;
  }[];
}

export interface VendorImage {
  id: number;
  fileName: string;
  s3Url: string;
  contentType: string;
  fileSize: number;
  vendorId: number;
  createdAt: string;
  showInExplore: boolean;
  displayOrder: number;
  imageMetadata: ImageMetadata;
  featured: boolean | null;
  spotlight: boolean | null;
}

export interface GooglePlaceReview {
  author_name: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
  profile_photo_url: string;
}

export interface GooglePlace {
  place_id: string;
  name: string;
  rating: number;
  user_ratings_total: number;
  formatted_address: string;
  website?: string;
  reviews: GooglePlaceReview[];
  photos?: Array<{
    photo_reference: string;
    height: number;
    width: number;
  }>;
  opening_hours?: {
    open_now: boolean;
    weekday_text: string[];
    periods: Array<{
      close: { day: number; time: string };
      open: { day: number; time: string };
    }>;
  };
}

export interface Vendor {
  id: number;
  name: string;
  vendorType: string;
  verified: boolean;
  topVendor: boolean;
  aboutAttributes: VendorAboutAttribute[];
  services: VendorServiceDescription[];
  address: string;
  phone: string;
  bio: string;
  locationBio: string;
  approximateCharge: number;
  email?: string;
  // Added fields to maintain compatibility with existing UI
  rating?: number;
  location?: string;
  price?: string;
  specialty?: string;
  availability?: string;
  image?: string;
  capacity?: number;
  // New field for vendor images
  vendorImages?: VendorImage[];
  // New fields for Google Places data
  placeId?: string | null;
  googlePlaceData?: GooglePlace | null;
}

export interface Category {
  name: string;
  color: string;
  displayName?: string;
}

// Data interfaces for content sections
export interface Stat {
  label: string;
  value: string;
}

export interface CategoryContent {
  title: string;
  description: string;
  stats: Stat[];
  actions: string[];
}

// Fetch vendor images
export const fetchVendorImages = async (vendorId: number): Promise<VendorImage[]> => {
  try {
    const response = await fetch(`https://wedify.be/api/vendor-images/vendors/${vendorId}`, {
      headers: {
        'X-Auth-Token': STATIC_TOKEN
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch vendor images');
    }
    
    const images: VendorImage[] = await response.json();
    return images.sort((a, b) => a.displayOrder - b.displayOrder);
  } catch (error) {
    console.error('Error fetching vendor images:', error);
    return [];
  }
};

// Function to fetch Google Place details
export const fetchGooglePlaceDetails = async (placeId: string): Promise<GooglePlace | null> => {
  try {
    console.log('🔍 Fetching place details for placeId:', placeId);
    const placeData = await getPlaceDetails(placeId);
    
    if (!placeData) {
      console.log('❌ No place details returned');
      return null;
    }

    console.log('✅ Place details received:', {
      name: placeData.name,
      rating: placeData.rating,
      totalReviews: placeData.user_ratings_total,
      reviewCount: placeData.reviews?.length,
      hasPhotos: placeData.photos?.length > 0
    });
    return placeData;
  } catch (error) {
    console.error('❌ Error fetching place details:', error);
    return null;
  }
};

// Function to search and get place details for a vendor
export const getVendorGoogleData = async (vendor: Vendor): Promise<{ placeId: string | null; googlePlaceData: GooglePlace | null }> => {
  try {
    // First search for the place
    console.log('🔍 Searching for place:', vendor.name);
    const placeId = await searchPlace(vendor.name, 'The Colony');
    
    if (!placeId) {
      console.log('❌ No place ID found for vendor:', vendor.name);
      return { placeId: null, googlePlaceData: null };
    }

    console.log('✅ Found placeId for vendor:', { name: vendor.name, placeId });

    // Then get the place details
    const googlePlaceData = await fetchGooglePlaceDetails(placeId);
    
    if (!googlePlaceData) {
      console.log('❌ No place details found for vendor:', vendor.name);
      return { placeId, googlePlaceData: null };
    }

    console.log('✅ Successfully fetched Google data for vendor:', {
      name: vendor.name,
      placeId,
      rating: googlePlaceData.rating,
      totalReviews: googlePlaceData.user_ratings_total,
      hasReviews: googlePlaceData.reviews?.length > 0
    });
    
    return { placeId, googlePlaceData };
  } catch (error) {
    console.error('❌ Error getting Google data for vendor:', vendor.name, error);
    return { placeId: null, googlePlaceData: null };
  }
};

export const fetchVendor = async (vendorId: number) => {
  try {
    const response = await fetch(`https://wedify.be/api/vendors/${vendorId}`, {
      headers: {
        'X-Auth-Token': STATIC_TOKEN
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch vendor');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching vendor:', error);
    return null;
  }
};

// Update fetchVendors to NOT include Google Places data
export const fetchVendors = async (): Promise<Vendor[]> => {
  try {
    console.log('Fetching vendors from API...');
    const response = await fetch('https://wedify.be/api/vendors', {
      headers: {
        'X-Auth-Token': STATIC_TOKEN
      }
    });
    
    if (!response.ok) {
      console.error('Failed to fetch vendors:', response.status, response.statusText);
      throw new Error('Failed to fetch vendors');
    }
    
    const vendors: Vendor[] = await response.json();
    
    // Log only Photography vendors with their verification status
    const photographyVendors = vendors.filter(v => 
      v.vendorType === 'PHOTOGRAPHY' || 
      v.vendorType === 'PHOTOGRAPHERS' || 
      v.vendorType === 'PHOTOGRAPHER'
    );
    
    console.log('Photography Vendors from API:', photographyVendors.map(v => ({
      name: v.name,
      type: v.vendorType,
      verified: v.verified,
      topVendor: v.topVendor,
      id: v.id
    })));
    
    // Process vendors without Google Places data
    const processedVendors = await Promise.all(vendors.map(async vendor => {
      try {
        // Fetch images for each vendor
        const vendorImages = await fetchVendorImages(vendor.id);
        const featuredImage = vendorImages.find(img => img.featured) || vendorImages[0];
        
        // Log vendor type for WorldByPixels
        if (vendor.name.toLowerCase().includes('worldbypixels')) {
          console.log('Processing WorldByPixels:', {
            name: vendor.name,
            type: vendor.vendorType,
            originalType: vendor.vendorType
          });
        }
        
        return {
          ...vendor,
          rating: 4.5 + Math.random() * 0.5, // Default rating until Google data is loaded
          location: vendor.address.split(',').slice(-2).join(',').trim(),
          price: '$'.repeat(Math.ceil(vendor.approximateCharge / 1000)),
          specialty: vendor.aboutAttributes[0]?.aboutItemDescription.split(' ')[0] || 'Premium Service',
          availability: 'Year-round',
          image: featuredImage?.s3Url || `/themesOption/${vendor.vendorType === 'VENUES' ? 'Luxury.jpg' : 'Modern.jpg'}`,
          vendorImages,
          capacity: vendor.vendorType === 'VENUES' ? Math.floor(Math.random() * 300) + 100 : undefined
        };
      } catch (error) {
        console.error(`Error processing vendor ${vendor.name}:`, error);
        return vendor;
      }
    }));
    
    console.log('Processed vendors:', {
      totalProcessed: processedVendors.length,
      vendorTypes: [...new Set(processedVendors.map(v => v.vendorType))],
      vendorNames: processedVendors.map(v => v.name),
      worldByPixels: processedVendors.find(v => v.name.toLowerCase().includes('worldbypixels'))
    });
    
    return processedVendors;
  } catch (error) {
    console.error('Error fetching vendors:', error);
    return [];
  }
};

export const organizeVendorsByType = (vendors: Vendor[]): Record<string, Vendor[]> => {
  const vendorsByType: Record<string, Vendor[]> = {};
  
  vendors.forEach(vendor => {
    // Log vendorType and name for debugging
    if (vendor.name.toLowerCase().includes('worldbypixels')) {
      console.log('[organizeVendorsByType] WorldByPixels vendorType:', vendor.vendorType, 'name:', vendor.name);
    }
    
    // Normalize the vendor type to match category names
    let type = vendor.vendorType;
    if (type === 'PHOTOGRAPHY' || type === 'PHOTOGRAPHERS' || type === 'PHOTOGRAPHER') {
      type = 'Photography';
    } else if (type === 'VENUES' || type === 'VENUE') {
      type = 'Venues';
    } else if (type === 'DECOR' || type === 'DECORATION') {
      type = 'Decor';
    } else if (type === 'FLORIST' || type === 'FLORISTS') {
      type = 'Florists';
    } else if (type === 'ENTERTAINMENT' || type === 'DJ' || type === 'BAND') {
      type = 'Entertainment';
    } else if (type === 'CATERING' || type === 'CATERERS') {
      type = 'Catering';
    } else if (type === 'TRANSPORT' || type === 'TRANSPORTATION') {
      type = 'Transport';
    } else if (type === 'PLANNER' || type === 'WEDDING_PLANNER' || type === 'PLANNERS') {
      type = 'Planners';
    } else {
      // For any other type, capitalize first letter and lowercase the rest
      type = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
    
    if (!vendorsByType[type]) {
      vendorsByType[type] = [];
    }
    vendorsByType[type].push(vendor);
  });
  
  // Log the organized vendors for debugging
  console.log('Organized vendors by type:', Object.entries(vendorsByType).map(([type, vendors]) => ({
    type,
    count: vendors.length,
    vendors: vendors.map(v => v.name)
  })));
  
  return vendorsByType;
}; 